import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_GatewayMapResponseDados } from '../contracts/Subscriptions_GatewayMapResponse'
import type { Subscriptions_GatewayToggleInput } from '../contracts/Subscriptions_GatewayToggleInput'
import type { Subaccounts_GatewayTestTriggerInput } from '../contracts/Subaccounts_GatewayTestTriggerInput'
import type { Subaccounts_GatewayTestTriggerResponseDados } from '../contracts/Subaccounts_GatewayTestTriggerResponse'
import type { Subaccounts_AsaasTriggerInput } from '../contracts/Subaccounts_AsaasTriggerInput'
import type { Subaccounts_AsaasTriggerResponseDados } from '../contracts/Subaccounts_AsaasTriggerResponse'
import type { Subscriptions_GatewaySettingsRow } from '../contracts/Subscriptions_GatewaySettingsRow'
import type { Subscriptions_GatewaySettingsInput } from '../contracts/Subscriptions_GatewaySettingsInput'
// Import side-effects: registers contracts with the responseContract registry.
import '../contracts/Subscriptions_GatewayMapResponse'
import '../contracts/Subscriptions_GatewayToggleInput'
import '../contracts/Subscriptions_GatewayToggleResponse'
import '../contracts/Subaccounts_GatewayTestTriggerInput'
import '../contracts/Subaccounts_GatewayTestTriggerResponse'
import '../contracts/Subaccounts_AsaasTriggerInput'
import '../contracts/Subaccounts_AsaasTriggerResponse'
import '../contracts/Subscriptions_GatewaySettingsResponse'
import '../contracts/Subscriptions_GatewaySettingsInput'

/**
 * usePaymentGatewayRegistry — SOLE network owner for subscriptions/gateways/* routes.
 *
 * This composable manages the subscription/credit gateway registry (P1).
 * DO NOT conflate with usePaymentProviders (subaccount/recebimentos ASAAS-Stripe config).
 *
 * The registered provider list is NOT hardcoded here — it is whatever the
 * backend map declares (gatewaytest + stripe today; asaas when P4 lands).
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const featureMap = ref<Subscriptions_GatewayMapResponseDados['features'] | null>(null)
const isLoading = ref(false)
const gatewaySettings = ref<Subscriptions_GatewaySettingsRow[] | null>(null)
const settingsLoading = ref(false)

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function usePaymentGatewayRegistry() {
  /**
   * loadMap — GET subscriptions/gateways/map.
   * Populates featureMap with the full enablement map.
   */
  async function loadMap(): Promise<void> {
    isLoading.value = true
    try {
      const raw = await httpService.get(
        sistemaUrl('subscriptions/gateways/map'),
        { responseContract: 'Subscriptions_GatewayMapResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: Subscriptions_GatewayMapResponseDados }
      featureMap.value = resp.dados.features
      log('usePaymentGatewayRegistry', 'loadMap', 'Gateway map loaded', {
        features: Object.keys(featureMap.value ?? {}),
      })
    } catch (err) {
      log('usePaymentGatewayRegistry', 'loadMap', 'Erro ao carregar mapa de gateways', {
        message: (err as Error)?.message,
      })
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * toggle — POST subscriptions/gateways/toggle {feature, provider_code, enabled}.
   * Flips a feature×provider enablement row, then refreshes the map.
   */
  async function toggle(
    feature: string,
    provider_code: string,
    enabled: boolean,
  ): Promise<void> {
    const body: Subscriptions_GatewayToggleInput = { feature, provider_code, enabled }
    try {
      await httpService.post(
        sistemaUrl('subscriptions/gateways/toggle'),
        body,
        {
          inputContract: 'Subscriptions_GatewayToggleInput',
          responseContract: 'Subscriptions_GatewayToggleResponse',
          skipErrorDisplay: false,
          // Recusa em HTTP 200: sem isto o toggle "salva", o loadMap() seguinte
          // repõe o valor antigo e a matriz pisca de volta sem explicação.
          throwOnEnvelopeFailure: 'gateway_toggle_failed',
        },
      )
      log('usePaymentGatewayRegistry', 'toggle', 'Toggle posted, refreshing map', {
        feature,
        provider_code,
        enabled,
      })
      await loadMap()
    } catch (err) {
      log('usePaymentGatewayRegistry', 'toggle', 'Erro ao alternar gateway', {
        message: (err as Error)?.message,
        feature,
        provider_code,
        enabled,
      })
      throw err
    }
  }

  /**
   * fireFakeTrigger — POST subaccounts/webhooks/gatewaytest-staff (porta ASSINADA).
   *
   * Até 2026-07-29 esta chamada batia no receptor CRU
   * (`subaccounts/webhooks/gatewaytest`) e respondia 401 por desenho: aquele
   * endpoint exige um header HMAC que nenhum browser consegue emitir (o
   * webhook_secret é rotate-only e mascarado em toda leitura). A porta assinada
   * do S3b resolve isso sem afrouxar nada — ela é Full-only, recusa fora de dev,
   * assina o corpo NO SERVIDOR e reentra no MESMO caminho de verificação, de
   * modo que a checagem de assinatura continua load-bearing.
   *
   * Skill: gatewaytest-console.
   */
  async function fireFakeTrigger(
    event_type: 'ONE_OFF_CHARGE_CONFIRMED' | 'ONE_OFF_CHARGE_FAILED',
    charge_id: string,
  ): Promise<Subaccounts_GatewayTestTriggerResponseDados> {
    const body: Subaccounts_GatewayTestTriggerInput = { event_type, charge_id }
    try {
      // O RESULTADO volta ao chamador (antes esta função era Promise<void> e o
      // descartava). O contrato existe justamente para o painel não ter que
      // adivinhar a partir de um 200 nu: 'ignored' chega DENTRO de um envelope
      // de sucesso — nada falhou, o evento só não confirmou nada — e reportar
      // isso como sucesso ensina o operador a confiar num botão que não fez
      // nada. Mordeu na franquia 14 em 2026-08-21.
      const raw = await httpService.post(
        sistemaUrl('subaccounts/webhooks/gatewaytest-staff'),
        body,
        {
          inputContract: 'Subaccounts_GatewayTestTriggerInput',
          responseContract: 'Subaccounts_GatewayTestTriggerResponse',
          skipErrorDisplay: false,
          // 'charge_not_found' / 'link_not_found' voltam em HTTP 200: um
          // disparo que não achou a cobrança não pode virar "evento enviado".
          throwOnEnvelopeFailure: 'gatewaytest_trigger_failed',
        },
      )
      const resp = raw as { dados: Subaccounts_GatewayTestTriggerResponseDados }
      log('usePaymentGatewayRegistry', 'fireFakeTrigger', 'GatewayTest webhook disparado', {
        event_type,
        charge_id,
        message: resp.dados?.message,
      })
      return resp.dados
    } catch (err) {
      log('usePaymentGatewayRegistry', 'fireFakeTrigger', 'Erro ao disparar webhook do GatewayTest', {
        message: (err as Error)?.message,
        event_type,
        charge_id,
      })
      throw err
    }
  }

  /**
   * fireAsaasTrigger — POST subaccounts/webhooks/asaas-staff (porta de dev).
   *
   * Simula uma entrega de webhook do ASAAS para uma cobrança que já está
   * pendente nos nossos livros. O servidor monta o payload no formato do ASAAS
   * e o empurra pelo MESMO caminho normalize -> dispatch de uma entrega real —
   * não há atalho, e o token continua sendo verificado.
   *
   * POR QUE ESTA RETORNA O `dados` E A DO GATEWAYTEST NÃO. Uma cobrança que a
   * plataforma não possui volta como `message: 'ignored'` DENTRO de um envelope
   * de sucesso — nada falhou, o evento apenas não tinha alvo. Sem ler o corpo,
   * quem chama não consegue distinguir isso de uma confirmação de verdade e
   * exibiria "webhook enviado" para um clique que não moveu nada. Por isso a
   * decisão de mensagem fica com o chamador, com o resultado em mãos.
   *
   * @param event_type Nome do evento no vocabulário do ASAAS.
   * @param charge_id  provider_charge_id da transação (pay_...).
   */
  async function fireAsaasTrigger(
    event_type: 'PAYMENT_CONFIRMED' | 'PAYMENT_OVERDUE',
    charge_id: string,
    value_cents?: number,
  ): Promise<Subaccounts_AsaasTriggerResponseDados> {
    const body: Subaccounts_AsaasTriggerInput = {
      event_type,
      charge_id,
      ...(value_cents != null ? { value_cents } : {}),
    }

    try {
      const raw = await httpService.post(
        sistemaUrl('subaccounts/webhooks/asaas-staff'),
        body,
        {
          inputContract: 'Subaccounts_AsaasTriggerInput',
          responseContract: 'Subaccounts_AsaasTriggerResponse',
          skipErrorDisplay: false,
          // 401/403/404/422 chegam como envelope de recusa; viram erro aqui.
          throwOnEnvelopeFailure: 'asaas_trigger_failed',
        },
      )
      const resp = raw as { dados: Subaccounts_AsaasTriggerResponseDados }

      log('usePaymentGatewayRegistry', 'fireAsaasTrigger', 'Webhook do ASAAS simulado', {
        event_type,
        charge_id,
        lane: resp.dados?.lane,
        message: resp.dados?.message,
      })

      return resp.dados
    } catch (err) {
      log('usePaymentGatewayRegistry', 'fireAsaasTrigger', 'Erro ao simular webhook do ASAAS', {
        message: (err as Error)?.message,
        event_type,
        charge_id,
      })
      throw err
    }
  }

  /**
   * loadGatewaySettings — GET subscriptions/gateways/settings/{provider_code}.
   * Populates gatewaySettings with the provider's (setting_key, setting_value)
   * rows. webhook_secret comes back MASKED from this endpoint by design.
   */
  async function loadGatewaySettings(provider_code: string): Promise<void> {
    settingsLoading.value = true
    try {
      const resp = await httpService.get(
        sistemaUrl(`subscriptions/gateways/settings/${provider_code}`),
        { responseContract: 'Subscriptions_GatewaySettingsResponse', skipErrorDisplay: false },
      )
      gatewaySettings.value = resp.dados?.settings ?? []
      log('usePaymentGatewayRegistry', 'loadGatewaySettings', 'Settings loaded', {
        provider_code,
        keys: (gatewaySettings.value ?? []).map(r => r.setting_key),
      })
    } catch (err) {
      log('usePaymentGatewayRegistry', 'loadGatewaySettings', 'Erro ao carregar configurações do gateway', {
        message: (err as Error)?.message,
        provider_code,
      })
      throw err
    } finally {
      settingsLoading.value = false
    }
  }

  /**
   * saveGatewaySetting — POST subscriptions/gateways/settings.
   *
   * The endpoint writes exactly ONE key per call and echoes the whole set back,
   * but that echo is DELIBERATELY DISCARDED: it reflects the row set as it stood
   * BEFORE the sibling keys of a multi-key save were written, so publishing it
   * into gatewaySettings mid-loop would push stale values back into any form
   * hydrated from this ref and silently revert the user's edits. Callers writing
   * several keys must call loadGatewaySettings() once at the end of the loop.
   */
  async function saveGatewaySetting(
    provider_code: string,
    setting_key: string,
    setting_value: string,
  ): Promise<void> {
    const body: Subscriptions_GatewaySettingsInput = { provider_code, setting_key, setting_value }
    try {
      await httpService.post(
        sistemaUrl('subscriptions/gateways/settings'),
        body,
        {
          inputContract: 'Subscriptions_GatewaySettingsInput',
          responseContract: 'Subscriptions_GatewaySettingsResponse',
          skipErrorDisplay: false,
          // A chave recusada (retirada, desconhecida, fora do ambiente de dev)
          // volta em HTTP 200. Sem isto o painel diz "Configurações salvas" e o
          // knob never_confirm continua valendo o valor antigo — e é justamente
          // esse knob que a suíte de relógio do R1 usa como rede de segurança.
          throwOnEnvelopeFailure: 'gateway_setting_save_failed',
        },
      )
      log('usePaymentGatewayRegistry', 'saveGatewaySetting', 'Setting saved', {
        provider_code,
        setting_key,
      })
    } catch (err) {
      log('usePaymentGatewayRegistry', 'saveGatewaySetting', 'Erro ao salvar configuração do gateway', {
        message: (err as Error)?.message,
        provider_code,
        setting_key,
      })
      throw err
    }
  }

  /**
   * rotateGatewaySecret — POST subscriptions/gateways/rotate-secret.
   *
   * Returns the new webhook_secret UNMASKED — this is the only moment the
   * backend ever emits the full value, so the caller must surface it once.
   * Afterwards the settings are reloaded so the UI shows the masked form again.
   */
  async function rotateGatewaySecret(provider_code: string): Promise<string> {
    const body: Subscriptions_GatewaySettingsInput = { provider_code }
    try {
      const resp = await httpService.post(
        sistemaUrl('subscriptions/gateways/rotate-secret'),
        body,
        {
          inputContract: 'Subscriptions_GatewaySettingsInput',
          responseContract: 'Subscriptions_GatewaySettingsResponse',
          skipErrorDisplay: false,
          // Uma rotação recusada devolveria a string vazia por causa do `??` —
          // o operador copiaria "nada" acreditando ser o novo segredo.
          throwOnEnvelopeFailure: 'gateway_secret_rotate_failed',
        },
      )
      const secret = resp.dados?.settings?.[0]?.setting_value ?? ''
      log('usePaymentGatewayRegistry', 'rotateGatewaySecret', 'Secret rotated', { provider_code })
      await loadGatewaySettings(provider_code)
      return secret
    } catch (err) {
      log('usePaymentGatewayRegistry', 'rotateGatewaySecret', 'Erro ao rotacionar segredo do gateway', {
        message: (err as Error)?.message,
        provider_code,
      })
      throw err
    }
  }

  /**
   * currenciesForProvider — returns the ISO 4217 currency codes supported by a
   * given provider across ALL features in the current featureMap.
   *
   * The currencies list is declared per-feature per-provider in the map, but
   * GatewayCapabilities ties currencies to the provider, not to a feature, so
   * the same list appears under every feature for that provider. We take the
   * union across all features so callers don't have to pick a feature name.
   *
   * Returns [] if featureMap is not yet loaded or the provider is unknown.
   * Returns ['*'] for providers that accept any currency (e.g. 'gatewaytest').
   */
  function currenciesForProvider(provider_code: string): string[] {
    if (!featureMap.value) return []
    const seen = new Set<string>()
    for (const featureProviders of Object.values(featureMap.value)) {
      const entry = featureProviders[provider_code]
      if (entry?.currencies) {
        for (const c of entry.currencies) seen.add(c)
      }
    }
    return Array.from(seen)
  }

  /**
   * _reset — clears module-scope state for test isolation (call in afterEach).
   */
  function _reset(): void {
    featureMap.value = null
    isLoading.value = false
    gatewaySettings.value = null
    settingsLoading.value = false
  }

  return {
    featureMap,
    isLoading,
    gatewaySettings,
    settingsLoading,
    loadMap,
    toggle,
    fireFakeTrigger,
    fireAsaasTrigger,
    loadGatewaySettings,
    saveGatewaySetting,
    rotateGatewaySecret,
    currenciesForProvider,
    _reset,
  }
}
